import axiosInstance from '../axios';
import { apiBaseURL } from 'config/axios';
import Utils from 'services/Utils';
import { CalendarEventGet, mapResultToCalendarEventGet } from '../CalendarService';
import { EstadoServicio, TipoServicio } from '../ServiciosService';

const entityApiRoute = 'cliente';

export interface TipoCertificado {
  id: number;
  key: string;
  description: string;
  color: string;
  foreColor: string;
  created_at: Date;
  updated_at: Date;
}

export interface Certificado {
  id: number;
  cliente_id: number;
  cantidad: number;
  avisoPorMail: number;
  imprimirFechaDeVencimiento: number;
  protocolo: string;
  fechaAno: string;
  fechaMes: string;
  fechaTratamiento: Date;
  fechaVencimiento: Date;
  fileName: string;
  tipo: string;
  url: string;
  created_at: Date;
  updated_at: Date;
  tipo_certificado: TipoCertificado;
}

export interface Cliente {
  id: number;
  user_id: number;
  nombre: string;
  domicilio: string;
  domicilioNormalizado: string;
  localidad: string;
  telefonoNotificacion: string;
  emailNotificacion: string;
  encargadoEmail: string;
  encargadoNombre: string;
  encargadoTelefono: string;
  estado: string;
  facturaCuit: string;
  facturaPrecio?: any;
  facturaRazonSocial: string;
  facturaTipo: string;
  observaciones: string;
  geoLat: number;
  geoLong: number;
  created_at: Date;
  updated_at: Date;
  certificados: Certificado[];
  servicios: CalendarEventGet[];
  encuestas: Encuesta[];
}

export interface Encuesta {
  id: number;
  googleEventId: string;
  fechaServicio: Date;
  operadores: number[];
  user_id: number;
  cliente_id: number;
  realizada?: any;
  horario?: any;
  equipamiento?: any;
  presencia?: any;
  actitud?: any;
  general?: any;
  comentario?: any;
  created_at: Date;
  updated_at: Date;
  cliente: Cliente;
}

export interface UserData {
  id: number;
  name: string;
  email: string;
  email_verified_at: Date;
  role: string;
  created_at: Date;
  updated_at: Date;
  deleted_at?: any;
  clientes: Cliente[];
  proximosServicios: CalendarEventGet[];
  operadores: Operador[];
  tipoServicio: TipoServicio[];
  estadoServicio: EstadoServicio[];
  encuestas: Encuesta[];
}

interface Operador {
  id: number;
  nombre: string;
  dni: string;
  imagen: string;
}

export interface EncuestaPost {
  horario: number;
  equipamiento: number;
  presencia: number;
  actitud: number;
  general: number;
  comentario: string;
}

const fetchOne = async (id: string): Promise<Certificado | undefined> => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  try {
    const response = await axiosInstance.get(url);
    let entity = response.data?.result;
    entity = Utils.removeNullValues(entity);
    return entity;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos con el ID proporcionado");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

const fetchAll = async () => {
  const url = `${apiBaseURL}/${entityApiRoute}/certificados`;
  const response = await axiosInstance.get(url);
  const result: Certificado[] = response.data?.result;
  return result;
}

const fetchTiposDeCertificado = async () => {
  const url = `${apiBaseURL}/tipos-certificado`;
  const response = await axiosInstance.get(url);
  const result: TipoCertificado[] = response.data?.result;
  return result;
}

const fetchUserData = async (): Promise<UserData> => {
  const url = `${apiBaseURL}/${entityApiRoute}/user`;
  try {
    const response = await axiosInstance.get(url);
    let entity = response.data?.result;
    entity = Utils.removeNullValues(entity);
    if (!entity.email.includes('@drfumigaciones.com.ar')) {
      const proximosServicios = await fetchProximosServicios();
      entity.proximosServicios = proximosServicios;
    } else {
      entity.proximosServicios = [];
    }
    return entity;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

const fetchServicios = async (params): Promise<CalendarEventGet[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/servicios`;
  const response = await axiosInstance.get(url, { params: { ...params } });
  const tempResult: any[] = response.data?.result;
  const result = tempResult.map((googleEvent) => {
    const entity = googleEvent.googleEvent;
    return mapResultToCalendarEventGet(entity);
  });
  return result;
}

const fetchProximosServicios = async (): Promise<CalendarEventGet[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/servicios/proximos`;
  const response = await axiosInstance.get(url);
  const tempResult: any[] = response.data?.result;
  const result = tempResult.map(entity => {
    return mapResultToCalendarEventGet(entity);
  });
  //ordena por fecha
  const sortedResult = result.slice().sort(
    (a, b) => a.startDate.getTime() - b.startDate.getTime()
  );
  return sortedResult;
}

const completarEncuesta = async (id: string, entity: EncuestaPost) => {
  const url = `${apiBaseURL}/${entityApiRoute}/encuestas/${id}`;
  await axiosInstance.patch(url, entity);
}

export default {
  fetchOne,
  fetchAll,
  fetchTiposDeCertificado,
  fetchUserData,
  fetchServicios,
  fetchProximosServicios,
  completarEncuesta
}
